import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { Check } from 'lucide-react';
import MatthewHenryVerseExcerpt from './MatthewHenryVerseExcerpt';

const HIGHLIGHT_COLORS = [
  { id: 'yellow', name: 'Yellow', bg: 'bg-yellow-200', text: 'text-yellow-900', hex: '#fef08a' },
  { id: 'green', name: 'Green', bg: 'bg-green-200', text: 'text-green-900', hex: '#bbf7d0' },
  { id: 'blue', name: 'Blue', bg: 'bg-blue-200', text: 'text-blue-900', hex: '#bfdbfe' },
  { id: 'purple', name: 'Purple', bg: 'bg-purple-200', text: 'text-purple-900', hex: '#e9d5ff' },
  { id: 'pink', name: 'Pink', bg: 'bg-pink-200', text: 'text-pink-900', hex: '#fbcfe8' },
  { id: 'orange', name: 'Orange', bg: 'bg-orange-200', text: 'text-orange-900', hex: '#fed7aa' },
  { id: 'red', name: 'Red', bg: 'bg-red-200', text: 'text-red-900', hex: '#fecaca' },
  { id: 'teal', name: 'Teal', bg: 'bg-teal-200', text: 'text-teal-900', hex: '#99f6e4' },
];

/**
 * Verse highlight picker. Selecting a color auto-applies and closes.
 *
 * New (v39): when the verse object has `book`/`chapter`/`verseNumber`, the
 * dialog also surfaces a Matthew Henry excerpt that covers this verse plus
 * a one-tap "Save as note" button. The button calls `onApplyHighlightWithNote`
 * (currentColor or 'yellow' default + commentary text + reference). Parents
 * unaware of `onApplyHighlightWithNote` simply lose this affordance —
 * existing color-picking flow stays untouched.
 */
const VerseHighlightDialog = ({
  isOpen,
  onClose,
  verse,
  onApplyHighlight,
  onApplyHighlightWithNote,
  currentColor,
}) => {
  const { language } = useLanguage();
  const [selectedColor, setSelectedColor] = useState(currentColor || 'yellow');

  const handleColorSelect = (colorId) => {
    setSelectedColor(colorId);
    onApplyHighlight(colorId);
    onClose();
  };

  const handleRemove = () => {
    onApplyHighlight(null);
    onClose();
  };

  const handleSaveAsNote = (excerpt) => {
    if (typeof onApplyHighlightWithNote !== 'function') return;
    const colorToUse = currentColor || selectedColor || 'yellow';
    const noteHeader = language === 'es'
      ? `Matthew Henry · ${excerpt.bookRef}`
      : `Matthew Henry · ${excerpt.bookRef}`;
    const note = `${noteHeader}\n\n${excerpt.commentary}`;
    onApplyHighlightWithNote(colorToUse, note);
    onClose();
  };

  if (!verse) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {language === 'es' ? 'Resaltar Versículo' : 'Highlight Verse'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Verse Preview */}
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
            <p className="text-sm font-semibold">{verse.reference}</p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 italic">
              "{verse.text}"
            </p>
          </div>

          {/* Color Selection */}
          <div>
            <p className="text-sm font-medium mb-3">
              {language === 'es' ? 'Seleccionar Color:' : 'Select Color:'}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {HIGHLIGHT_COLORS.map((color) => (
                <button
                  key={color.id}
                  onClick={() => handleColorSelect(color.id)}
                  className={`h-14 rounded-lg border-2 transition-all relative ${color.bg} ${
                    selectedColor === color.id
                      ? 'border-purple-600 scale-105 shadow-lg'
                      : 'border-slate-300 dark:border-slate-600 hover:border-purple-400'
                  }`}
                >
                  <span className={`text-xs font-semibold ${color.text}`}>
                    {color.name}
                  </span>
                  {selectedColor === color.id && (
                    <div className="absolute top-1 right-1 bg-purple-600 rounded-full p-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Matthew Henry excerpt + "save as note" — only when we have
              enough verse metadata to look up a section */}
          {verse.book && verse.chapter && (
            <MatthewHenryVerseExcerpt
              book={verse.book}
              chapter={verse.chapter}
              verse={verse.verseNumber || verse.verse || 0}
              language={language}
              onSelect={handleSaveAsNote}
            />
          )}

          {/* Remove button (only if already highlighted) */}
          {currentColor && (
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={handleRemove}
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {language === 'es' ? 'Quitar Resaltado' : 'Remove Highlight'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerseHighlightDialog;
export { HIGHLIGHT_COLORS };
